import React, { useCallback, useEffect, useState } from "react";
import api, { formatErrorDetail } from "@/lib/api";
import { Surface, Empty, TableWrap } from "@/components/app/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, ArrowUp, ArrowDown, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";
import { manilaToday } from "@/lib/date";

// The backend rejects next_run_at <= manilaToday() (a cadenced rotation must
// not be created already-due or back-dated) -- the picker's floor must be
// tomorrow, not today, or the earliest date it ever offers always 400s.
function minRotationDate() {
    return manilaToday(new Date(Date.now() + 24 * 60 * 60 * 1000));
}

// Mirrors services/rotation_rules.CADENCES on the backend.
const CADENCES = [
    { value: "manual", label: "Manual only" },
    { value: "weekly", label: "Weekly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "semi_annual", label: "Semi-annual" },
    { value: "annual", label: "Annual" },
];

const BLANK = { name: "", shift_ids: [], cadence: "manual", next_run_at: "" };

const STATUS_BADGE = { completed: "badge-approved", failed: "badge-rejected", in_progress: "badge-submitted" };

export default function ShiftRotations({ shifts }) {
    const [rotations, setRotations] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(BLANK);
    const [addShiftId, setAddShiftId] = useState("");

    // Preview-before-commit state for "Rotate Now" — never fires on the
    // first click. See spec addendum A6: the button previews what would
    // move and how many employees it reaches, and only an explicit confirm
    // calls fire().
    const [preview, setPreview] = useState(null); // { rotation, on_date, data, loading, firing }

    const [runsFor, setRunsFor] = useState(null); // rotation id whose history is expanded
    const [runs, setRuns] = useState([]);
    const [runsLoading, setRunsLoading] = useState(false);

    const load = useCallback(() => {
        api.get("/rotations").then((r) => setRotations(r.data.items || [])).catch(() => setRotations([]));
    }, []);
    useEffect(() => { load(); }, [load]);

    const shiftName = (id) => shifts.find((s) => s.id === id)?.name || id;
    const activeShifts = shifts.filter((s) => s.is_active !== false);

    const open = (r) => {
        setEditing(r || "new");
        setForm(r ? { name: r.name, shift_ids: [...(r.shift_ids || [])],
                      cadence: r.cadence || "manual", next_run_at: r.next_run_at || "" }
                  : BLANK);
        setAddShiftId("");
    };

    const addToRing = () => {
        if (!addShiftId || form.shift_ids.includes(addShiftId)) return;
        setForm((f) => ({ ...f, shift_ids: [...f.shift_ids, addShiftId] }));
        setAddShiftId("");
    };
    // Reordering is always explicit (up/down), never an implicit sort — the
    // stored ring order IS the rotation, so nothing here may silently
    // reorder it (e.g. alphabetically or by start time).
    const moveRing = (i, dir) => {
        setForm((f) => {
            const ids = [...f.shift_ids];
            const j = i + dir;
            if (j < 0 || j >= ids.length) return f;
            [ids[i], ids[j]] = [ids[j], ids[i]];
            return { ...f, shift_ids: ids };
        });
    };
    const removeFromRing = (i) => setForm((f) => ({ ...f, shift_ids: f.shift_ids.filter((_, idx) => idx !== i) }));

    const save = async () => {
        if (!form.name.trim()) return toast.error("Name is required");
        if (form.shift_ids.length < 2) return toast.error("A rotation needs at least two shifts in its ring");
        if (form.cadence !== "manual" && !form.next_run_at) {
            return toast.error("Pick the first scheduled date — a scheduled rotation must not fire "
                + "unattended with nobody having previewed it");
        }
        const body = { name: form.name.trim(), shift_ids: form.shift_ids, cadence: form.cadence,
                       next_run_at: form.cadence === "manual" ? null : form.next_run_at };
        try {
            if (editing === "new") await api.post("/rotations", body);
            else await api.put(`/rotations/${editing.id}`, body);
            toast.success(editing === "new" ? "Rotation created" : "Rotation updated");
            setEditing(null); load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const deactivate = async (r) => {
        if (!window.confirm(`Deactivate rotation "${r.name}"? It stops firing on schedule; past runs stay undoable.`)) return;
        try { await api.delete(`/rotations/${r.id}`); toast.success("Deactivated"); load(); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    // ---------------- Rotate Now: preview first, fire only on confirm ----------------
    const startPreview = async (r) => {
        const on_date = manilaToday();
        setPreview({ rotation: r, on_date, data: null, loading: true, firing: false });
        try {
            const { data } = await api.get(`/rotations/${r.id}/preview`, { params: { on_date } });
            setPreview({ rotation: r, on_date, data, loading: false, firing: false });
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail));
            setPreview(null);
        }
    };

    const confirmFire = async () => {
        if (!preview) return;
        setPreview((p) => ({ ...p, firing: true }));
        try {
            // fire() always either commits and returns 200 with
            // status: "completed", or raises -- per its own docstring in
            // services/rotation_service.py, the run row is written before
            // anything else can fail and updated in a `finally`, but the
            // function still re-raises on any exception afterward. A failed
            // fire therefore never reaches here as a 200 with
            // status: "failed" -- it arrives as a rejected promise, handled
            // by the catch below. No status branch needed on the happy path.
            const { data } = await api.post(`/rotations/${preview.rotation.id}/fire`, { on_date: preview.on_date });
            toast.success(`Rotation fired — ${data.employees_affected} employee(s) moved`);
            const firedRotationId = preview.rotation.id;
            setPreview(null); load();
            if (runsFor === firedRotationId) loadRuns(firedRotationId);
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail));
            setPreview((p) => (p ? { ...p, firing: false } : p));
        }
    };

    // ---------------- Run history ----------------
    const loadRuns = (rotationId) => {
        setRunsLoading(true);
        api.get(`/rotations/${rotationId}/runs`).then((r) => setRuns(r.data.items || []))
            .catch(() => setRuns([])).finally(() => setRunsLoading(false));
    };
    const toggleRuns = (r) => {
        if (runsFor === r.id) { setRunsFor(null); setRuns([]); return; }
        setRunsFor(r.id); loadRuns(r.id);
    };
    const undoRun = async (run) => {
        if (!window.confirm("Undo this rotation run? It restores every rule it moved to its prior shift.")) return;
        try {
            await api.post(`/rotations/runs/${run.id}/undo`);
            toast.success("Run undone");
            loadRuns(run.rotation_id); load();
        } catch (e) {
            // Surface the API's own refusal (e.g. "Cannot undo run ...: it did not
            // complete (status='failed', ...)") rather than a generic message —
            // the backend names exactly which assignment is orphaned.
            toast.error(formatErrorDetail(e.response?.data?.detail));
        }
    };

    return (
        <Surface className="p-4">
            <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-muted-foreground">
                    A rotation moves the assignment RULE, not the person — whoever a rule reaches at the
                    moment it fires is who moves. Rotate Now always previews the blast radius first;
                    nothing moves until you confirm.
                </div>
                <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2"
                        onClick={() => open(null)} data-testid="new-rotation-btn"><Plus className="size-4" /> New rotation</button>
            </div>

            <div className="grid gap-2">
                {rotations.map((r) => (
                    <div key={r.id} className={`rounded-lg border p-3 ${r.is_active === false ? "opacity-50" : ""}`}
                         data-testid={`rotation-${r.id}`}>
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <div>
                                <b className="text-sm">{r.name}</b>
                                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1 flex-wrap">
                                    {(r.shift_ids || []).map((id, i) => (
                                        <React.Fragment key={`${id}-${i}`}>
                                            {i > 0 && <ArrowRight className="size-3" />}
                                            <span>{shiftName(id)}</span>
                                        </React.Fragment>
                                    ))}
                                    {r.shift_ids?.length > 0 && <>
                                        <ArrowRight className="size-3" />
                                        <span>{shiftName(r.shift_ids[0])}</span>
                                    </>}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                    {CADENCES.find((c) => c.value === r.cadence)?.label || r.cadence}
                                    {r.next_run_at ? ` · next ${r.next_run_at}` : ""}
                                    {r.last_fired_on ? ` · last fired ${r.last_fired_on}` : ""}
                                </div>
                            </div>
                            <div className="flex items-center gap-3 text-xs">
                                <button className="text-primary" onClick={() => open(r)} data-testid={`edit-rotation-${r.id}`}>Edit</button>
                                <button className="text-primary" onClick={() => startPreview(r)}
                                        data-testid={`rotate-now-${r.id}`}>Rotate Now</button>
                                <button className="text-primary" onClick={() => toggleRuns(r)} data-testid={`history-${r.id}`}>
                                    {runsFor === r.id ? "Hide history" : "History"}
                                </button>
                                {r.is_active !== false &&
                                    <button className="text-destructive" onClick={() => deactivate(r)} data-testid={`deactivate-rotation-${r.id}`}>Deactivate</button>}
                            </div>
                        </div>

                        {runsFor === r.id && (
                            <div className="mt-3">
                                {runsLoading ? <div className="text-xs text-muted-foreground py-2">Loading…</div> : (
                                    <TableWrap>
                                        <thead><tr><th>Fired</th><th>Trigger</th><th>Employees</th><th>Status</th><th></th></tr></thead>
                                        <tbody>
                                            {runs.map((run) => (
                                                <tr key={run.id} data-testid={`run-${run.id}`}>
                                                    <td className="font-mono text-xs">{run.fired_on}</td>
                                                    <td><span className="badge badge-info">{run.trigger}</span></td>
                                                    <td>{run.employees_affected}</td>
                                                    <td>
                                                        <span className={`badge ${STATUS_BADGE[run.status] || "badge-info"}`}>
                                                            {run.reverted_at ? "reverted" : (run.status || "completed")}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {!run.reverted_at &&
                                                            <button className="text-destructive text-xs" onClick={() => undoRun(run)}
                                                                    data-testid={`undo-run-${run.id}`}>Undo</button>}
                                                    </td>
                                                </tr>
                                            ))}
                                            {!runs.length && <tr><td colSpan={5}>
                                                <Empty title="No runs yet" subtitle="Fire this rotation to see history here." />
                                            </td></tr>}
                                        </tbody>
                                    </TableWrap>
                                )}
                            </div>
                        )}
                    </div>
                ))}
                {!rotations.length && <Empty title="No rotations yet"
                    subtitle="Build a ring of shifts to rotate people between them on a schedule." />}
            </div>

            {/* Create / edit — the ring is built by explicit add + reorder, never sorted implicitly. */}
            <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>{editing === "new" ? "New rotation" : `Edit ${form.name}`}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Name</Label><Input value={form.name} data-testid="rotation-name"
                            onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Warehouse 3-shift" /></div>

                        <div>
                            <Label>Ring — the order shifts rotate through</Label>
                            <div className="flex gap-2 mt-1">
                                <select className="h-9 flex-1 rounded-md border px-2 text-sm bg-background"
                                        value={addShiftId} data-testid="ring-add-shift"
                                        onChange={(e) => setAddShiftId(e.target.value)}>
                                    <option value="">Select a shift to add…</option>
                                    {activeShifts.filter((s) => !form.shift_ids.includes(s.id)).map((s) =>
                                        <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                                <button type="button" className="h-9 px-3 rounded-md border text-sm"
                                        onClick={addToRing} data-testid="ring-add-btn">Add</button>
                            </div>
                            <div className="space-y-1 mt-2">
                                {form.shift_ids.map((id, i) => (
                                    <div key={id} className="flex items-center gap-2 rounded-md border px-2 py-1 text-sm"
                                         data-testid={`ring-item-${i}`}>
                                        <span className="text-xs text-muted-foreground w-4">{i + 1}.</span>
                                        <span className="flex-1">{shiftName(id)}</span>
                                        <button type="button" className="text-muted-foreground disabled:opacity-30" disabled={i === 0}
                                                onClick={() => moveRing(i, -1)} aria-label="Move up"><ArrowUp className="size-3.5" /></button>
                                        <button type="button" className="text-muted-foreground disabled:opacity-30"
                                                disabled={i === form.shift_ids.length - 1}
                                                onClick={() => moveRing(i, 1)} aria-label="Move down"><ArrowDown className="size-3.5" /></button>
                                        <button type="button" className="text-destructive"
                                                onClick={() => removeFromRing(i)} aria-label="Remove"><X className="size-3.5" /></button>
                                    </div>
                                ))}
                                {form.shift_ids.length < 2 &&
                                    <div className="text-xs text-muted-foreground">Add at least two shifts, in the order they rotate.</div>}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <Label>Cadence</Label>
                                <select className="h-9 w-full rounded-md border px-2 text-sm bg-background"
                                        value={form.cadence} data-testid="rotation-cadence"
                                        onChange={(e) => setForm({ ...form, cadence: e.target.value,
                                            next_run_at: e.target.value === "manual" ? "" : form.next_run_at })}>
                                    {CADENCES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <Label>Next run date</Label>
                                <Input type="date" value={form.next_run_at || ""} disabled={form.cadence === "manual"}
                                       min={minRotationDate()} data-testid="rotation-next-run"
                                       onChange={(e) => setForm({ ...form, next_run_at: e.target.value })} />
                            </div>
                        </div>
                        {form.cadence !== "manual" &&
                            <div className="text-xs text-muted-foreground">
                                Required — a scheduled rotation must not fire unattended on the next cron tick
                                with nobody having previewed it.
                            </div>}
                    </div>
                    <DialogFooter>
                        <button className="h-9 px-3 rounded-md bg-primary text-white text-sm"
                                onClick={save} data-testid="rotation-save">Save</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Preview before commit — Rotate Now never fires on the first click (spec A6). */}
            <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>Preview: {preview?.rotation?.name}</DialogTitle></DialogHeader>
                    {preview?.loading ? (
                        <div className="text-sm text-muted-foreground py-6 text-center">Loading preview…</div>
                    ) : preview?.data ? (
                        <div className="space-y-3">
                            <div className="text-sm" data-testid="preview-employee-count">
                                This moves <b>{preview.data.employees_affected}</b> employee(s), effective{" "}
                                <span className="font-mono">{preview.data.on_date}</span>.
                            </div>
                            <TableWrap>
                                <thead><tr><th>Applies to</th><th>From</th><th></th><th>To</th></tr></thead>
                                <tbody>
                                    {preview.data.moves.map((m) => (
                                        <tr key={m.assignment_id} data-testid={`preview-move-${m.assignment_id}`}>
                                            <td><span className="badge badge-info">{m.target_type}</span> {m.target_label}</td>
                                            <td>{m.from_shift_name || m.from_shift_id}</td>
                                            <td><ArrowRight className="size-3.5" /></td>
                                            <td>{m.to_shift_name || m.to_shift_id}</td>
                                        </tr>
                                    ))}
                                    {!preview.data.moves.length && <tr><td colSpan={4}>
                                        <Empty title="Nothing to move" subtitle="No live rule currently points at a shift in this ring." />
                                    </td></tr>}
                                </tbody>
                            </TableWrap>
                        </div>
                    ) : null}
                    <DialogFooter>
                        <button className="h-9 px-3 rounded-md border text-sm" onClick={() => setPreview(null)}
                                data-testid="preview-cancel">Cancel</button>
                        <button className="h-9 px-3 rounded-md bg-primary text-white text-sm disabled:opacity-50"
                                onClick={confirmFire} data-testid="preview-confirm"
                                disabled={preview?.loading || preview?.firing || !preview?.data?.moves?.length}>
                            {preview?.firing ? "Firing…" : "Confirm — fire rotation"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Surface>
    );
}
